import { Button, Checkbox, Input, Label } from "@mixitone/components";
import ChevronDownIcon from "@mixitone/icons/chevron-down.svg";
import ChevronUpIcon from "@mixitone/icons/chevron-up.svg";
import { Account } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import {
  formatCurrencyCents,
  type FundingDonationTier,
  MAX_FUNDING_DONATION_TIERS,
  normalizeFundingDonationTiers,
} from "@mixitone/util";
import type { CustomComponentFieldProps } from "components/Forms/FormFieldsDefinition";
import { useCallback } from "react";

function centsToDollarsInput(cents: number): string {
  if (!Number.isFinite(cents)) return "";
  return (cents / 100).toFixed(2).replace(/\.?0+$/, "") || "0";
}

function dollarsInputToCents(raw: string): number {
  const trimmed = raw.trim().replace(/,/g, "");
  if (!trimmed) return 0;
  const value = Number.parseFloat(trimmed);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value * 100);
}

function moveTier(tiers: FundingDonationTier[], index: number, direction: -1 | 1): FundingDonationTier[] {
  const target = index + direction;
  if (target < 0 || target >= tiers.length) return tiers;
  const copy = [...tiers];
  [copy[index], copy[target]] = [copy[target], copy[index]];
  return copy;
}

function patchTier(
  tiers: FundingDonationTier[],
  index: number,
  patch: Partial<FundingDonationTier>,
): FundingDonationTier[] {
  return tiers.map((tier, i) => (i === index ? { ...tier, ...patch } : tier));
}

function newEmptyTier(): FundingDonationTier {
  return {
    id: crypto.randomUUID(),
    label: "",
    amount_cents: 2500,
    description: "",
    highlight: false,
  };
}

function tierSummary(tier: FundingDonationTier, index: number, currency: string): string {
  const amount = formatCurrencyCents(tier.amount_cents, currency);
  const name = tier.label.trim();
  return name ? `${amount} · ${name}` : `Tier ${index + 1}`;
}

export const FundingAppealDonationTiersField = ApplicationView(
  ({ value, onChange, field, record }: CustomComponentFieldProps) => {
    const disabled =
      typeof field.disabled === "function" ? field.disabled(record) : (field.disabled ?? false);
    const tiers = normalizeFundingDonationTiers(value, { applyDefaults: false });
    const displayTiers = tiers.length > 0 ? tiers : normalizeFundingDonationTiers(null);
    const currency = Account.current?.currency ?? "usd";

    const setTiers = useCallback(
      (next: FundingDonationTier[]) => {
        onChange(next.length > 0 ? next : []);
      },
      [onChange],
    );

    const addTier = () => {
      if (displayTiers.length >= MAX_FUNDING_DONATION_TIERS) return;
      setTiers([...displayTiers, newEmptyTier()]);
    };

    return (
      <div className="flex flex-col gap-3">
        {displayTiers.map((tier, index) => (
          <div
            key={tier.id}
            className="space-y-2 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4 shadow-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-sm font-medium text-gray-900">
                {tierSummary(tier, index, currency)}
              </span>
              <div className="flex shrink-0 items-center gap-0.5">
                <Button
                  kind="plain"
                  compact
                  type="button"
                  className="h-7 w-7 p-0 text-gray-500"
                  disabled={disabled || index === 0}
                  aria-label="Move tier up"
                  onClick={() => setTiers(moveTier(displayTiers, index, -1))}
                >
                  <ChevronUpIcon className="h-4 w-4" />
                </Button>
                <Button
                  kind="plain"
                  compact
                  type="button"
                  className="h-7 w-7 p-0 text-gray-500"
                  disabled={disabled || index >= displayTiers.length - 1}
                  aria-label="Move tier down"
                  onClick={() => setTiers(moveTier(displayTiers, index, 1))}
                >
                  <ChevronDownIcon className="h-4 w-4" />
                </Button>
                <Button
                  kind="link"
                  type="button"
                  className="h-7 px-2 text-xs text-gray-600"
                  disabled={disabled || displayTiers.length <= 1}
                  onClick={() => setTiers(displayTiers.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </div>
            </div>
            <div className="grid gap-2 @sm/app:grid-cols-[6.5rem_1fr] @sm/app:items-end">
              <div>
                <Label className="mb-1 block text-xs text-gray-500">Amount</Label>
                <Input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={centsToDollarsInput(tier.amount_cents)}
                  disabled={disabled}
                  onChange={(e) =>
                    setTiers(
                      patchTier(displayTiers, index, {
                        amount_cents: dollarsInputToCents(e.target.value),
                      }),
                    )
                  }
                />
              </div>
              <div>
                <Label className="mb-1 block text-xs text-gray-500">Label</Label>
                <Input
                  value={tier.label}
                  disabled={disabled}
                  placeholder="e.g. Personal supporter"
                  onChange={(e) => setTiers(patchTier(displayTiers, index, { label: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs text-gray-500">Description</Label>
              <Input
                value={tier.description}
                disabled={disabled}
                placeholder="Short text for donors"
                onChange={(e) => setTiers(patchTier(displayTiers, index, { description: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id={`tier-highlight-${tier.id}`}
                checked={tier.highlight === true}
                disabled={disabled}
                onChange={(e) => setTiers(patchTier(displayTiers, index, { highlight: e.target.checked }))}
              />
              <label htmlFor={`tier-highlight-${tier.id}`} className="text-sm text-gray-700">
                Recommended
              </label>
            </div>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            kind="secondary"
            type="button"
            disabled={disabled || displayTiers.length >= MAX_FUNDING_DONATION_TIERS}
            onClick={addTier}
          >
            Add tier
          </Button>
        </div>
      </div>
    );
  },
);
